import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { portalConst } from '@app/config';
import { AlertService, LearningTrailsSectionService, LearningTrailsService, NotificationService } from '@app/core';
import { StatusEnum } from '@app/core/models/status.enum';
import { ElementsService } from '@app/core/services/elements.service';
import { SIDENAV_DATA, SIDENAV_REF, SidenavRef } from '@app/shared/modules/template/components/sidenav';
import { notNull } from '@app/shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { DropzoneConfigInterface } from 'ngx-dropzone-wrapper';
import { BehaviorSubject, switchMap, take } from 'rxjs';

@Component({
  templateUrl: './sidenav-learning-form-trails.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SidenavLearningFormTrailsComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly learningTrailsService = inject(LearningTrailsService);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly alertService = inject(AlertService);
  private readonly sidenavData = inject(SIDENAV_DATA);
  private readonly sidenavRef = inject<SidenavRef>(SIDENAV_REF);
  public readonly elementsService = inject(ElementsService);
  public readonly learningTrailsSectionService = inject(LearningTrailsSectionService);
  public readonly ckeditorUploadImageUrl = `${portalConst.api.usuario}/v2/upload/ckeditor-image?module=trilhas`;
  public submitted = false;
  public form: FormGroup;
  public config: DropzoneConfigInterface;
  public msgTypeView: string;
  public thumbnail$ = new BehaviorSubject<string>(undefined);

  public ngOnInit(): void {
    this.initForm();
    this.initConfigUpload();

    if (!this.form.value.id) {
      const data = this.sidenavData;
      if (data.route?.params?.course_id) {
        this.form.get('curso_id').setValue(data.route.params.course_id);
        if (data.route?.params?.subdivision_id) {
          this.form.get('subdivisao_id').setValue(data.route.params.subdivision_id);
        }
      } else if (data?.snapshot?.courseData?.data) {
        this.form.patchValue(data.snapshot.courseData.data);
        this.form.get('status').setValue(!!data.snapshot.courseData.data.status);
        this.form.get('carga_horaria').setValue(this.form.value.carga_horaria?.split(' ')[0]);
        this.thumbnail$.next(data.snapshot.courseData.data.thumbnail);
      }
    }

    this.msgTypeView = `<strong>${this.translateService.instant(
      'trilhas.estudio.tipo-visualizacao.linear-nao-guiado',
    )}</strong>: ${this.translateService.instant('trilhas.estudio.tipo-visualizacao.linear-nao-guiado-texto')}<br/>
    <strong>${this.translateService.instant(
      'trilhas.estudio.tipo-visualizacao.linear-guiado',
    )}</strong>: ${this.translateService.instant('trilhas.estudio.tipo-visualizacao.linear-guiado-texto')}<br/>
    <strong>${this.translateService.instant(
      'trilhas.estudio.tipo-visualizacao.agrupado',
    )}</strong>: ${this.translateService.instant('trilhas.estudio.tipo-visualizacao.agrupado-texto')}<br/>`;
  }

  public onSubmit(): void {
    this.form.markAllAsTouched();
    this.form.markAsPristine();
    this.submitted = true;
    if (this.form.valid) {
      this.submitted = false;
      if (this.form.value.id) {
        this.learningTrailsService.update(this.form.value.id, this.form.value).subscribe((resp) => {
          if (resp.ret === StatusEnum.Active) {
            this.notificationService.success(resp.msg);
            this.elementsService.changeStatus(this.form.value.status);
          } else {
            this.notificationService.error(resp.msg);
          }
        });
      } else {
        this.learningTrailsService.store(this.form.value).subscribe((resp) => {
          if (resp.ret === StatusEnum.Active) {
            this.notificationService.success(resp.msg);
            this.elementsService.changeStatus(this.form.value.status);
            const url = new URL(window.location.href).pathname.split('/');
            this.router.navigate([url[1], url[2], url[3], resp['slug']]);
          } else {
            this.notificationService.error(resp.msg);
          }
        });
      }
    }
  }

  public onUpload(file: any) {
    this.form.get('hash').setValue(file.hash);
    this.form.get('thumbnail').setValue(file.file);
    this.thumbnail$.next(`${portalConst.aws.s3Snv}/temp-upload-files/${file.file}`);
  }

  public onClose() {
    if (!!this.form.value.id) {
      this.sidenavRef.close();
    } else {
      this.location.back();
    }
  }

  public onDelete() {
    if (!!this.form.value.id) {
      this.alertService
        .confirm({ message: 'trilhas.estudio.trilha.deseja-excluir-trilha' })
        .pipe(
          take(1),
          notNull(),
          switchMap(() => this.learningTrailsService.delete(this.form.value.id)),
        )
        .subscribe({
          next: (resp) => {
            if (resp.ret === StatusEnum.Active) {
              this.notificationService.success(resp.msg);
              this.location.back();
            } else {
              this.notificationService.error(resp.msg);
            }
          },
          error: (error) => this.notificationService.error(error.error.msg),
        });
    } else {
      this.location.back();
    }
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      id: undefined,
      subdivisao_id: undefined,
      curso_id: [undefined, [Validators.required]],
      titulo: [undefined, [Validators.required, Validators.maxLength(70), Validators.minLength(3)]],
      carga_horaria: [0, [Validators.min(0), Validators.maxLength(3)]],
      descricao: [undefined],
      tipo: [0, [Validators.required]],
      thumbnail: [undefined, [Validators.required]],
      hash: undefined,
      fixar: 0,
      evento_id: undefined,
      sessao_id: undefined,
      ordem: undefined,
      status: false,
    });

    this.form.controls.titulo.valueChanges.subscribe(() => {
      this.form.controls.titulo.markAsTouched();
      this.form.controls.titulo.markAsPristine();
    });
  }

  private initConfigUpload(): void {
    this.config = {
      clickable: true,
      url: `${portalConst.api.usuario}/temp-upload/v1/file`,
      createImageThumbnails: false,
      maxFiles: 1,
      maxFilesize: 0.15,
      acceptedFiles: '.jpg,.jpeg,.png',
      previewsContainer: false,
      params: {
        savePhoto: true,
        largura: 500,
        altura: 500,
        limiteMaximo: true,
        pesoMaximo: 150,
        stringKbMb: 'kb',
      },
    };
  }
}
